import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PublicFormLayout } from '../components/PublicFormLayout';
import { FormSectionCard } from '../components/FormSectionCard';
import { ProgressSteps } from '../components/ProgressSteps';
import UploadCard from '../components/UploadCard';
import SuccessState from '../components/SuccessState';
import { AlertTriangle, ChevronRight, CheckCircle2, FileText, Shield, Phone } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCHOOL_STEPS = ['Perguruan', 'Guru/Wakil', 'Latihan', 'Dokumen', 'Hantar'];
const DIVISIONS = [
  'Kuching', 'Samarahan', 'Sri Aman', 'Betong', 'Sarikei',
  'Sibu', 'Mukah', 'Kapit', 'Bintulu', 'Miri', 'Limbang', 'Serian'
];

const FieldWrapper = ({ label, required, hint, children, testId }) => (
  <div>
    <label className="block text-xs font-bold text-[#0B0B0B] uppercase tracking-wide mb-1.5">
      {label}{required && <span className="text-[#D71920] ml-1">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-[#999] mt-1">{hint}</p>}
  </div>
);

const StyledInput = (props) => (
  <Input
    {...props}
    className={`h-11 border-[#E5E5E5] focus-visible:ring-1 focus-visible:ring-[#0B0B0B] focus-visible:ring-offset-0 focus-visible:border-[#0B0B0B] text-sm bg-white placeholder:text-[#bbb] ${props.className || ''}`}
  />
);

const StyledSelect = ({ value, onValueChange, placeholder, children }) => (
  <Select value={value} onValueChange={onValueChange}>
    <SelectTrigger className="h-11 border-[#E5E5E5] focus:ring-1 focus:ring-[#0B0B0B] focus:ring-offset-0 text-sm bg-white">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>{children}</SelectContent>
  </Select>
);

// Right sidebar
const SchoolSidebar = () => (
  <div className="space-y-4">
    {/* Process steps */}
    <div className="bg-white border border-[#E5E5E5] rounded overflow-hidden shadow-sm">
      <div className="px-5 py-3 bg-[#0B0B0B] flex items-center gap-2">
        <CheckCircle2 size={14} className="text-[#FFD21F]" />
        <span className="text-xs font-bold text-white uppercase tracking-widest">Proses Semakan</span>
      </div>
      <div className="p-5 space-y-3">
        {[
          { label: 'Pending', desc: 'Permohonan diterima' },
          { label: 'Disemak', desc: 'Sedang disemak oleh Sekretariat RSS' },
          { label: 'Diluluskan', desc: 'Perguruan aktif dalam sistem' },
        ].map((step, i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full border-2 border-[#E5E5E5] bg-white flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-[#0B0B0B]">{step.label}</p>
              <p className="text-xs text-[#999]">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Checklist */}
    <div className="bg-white border border-[#E5E5E5] rounded overflow-hidden shadow-sm">
      <div className="px-5 py-3 border-b border-[#E5E5E5]">
        <span className="text-xs font-bold text-[#0B0B0B] uppercase tracking-widest">Senarai Semak</span>
      </div>
      <div className="p-5 space-y-2.5">
        {[
          'Nama perguruan yang lengkap',
          'Nama guru / ketua perguruan',
          'Nombor telefon aktif',
          'Alamat emel yang sah',
          'Bahagian dan daerah',
          'Alamat gelanggang latihan',
          'Logo perguruan (opsional)',
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-2.5">
            <div className="w-4 h-4 border border-[#E5E5E5] rounded flex-shrink-0" />
            <span className="text-xs text-[#555]">{item}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Privacy */}
    <div className="bg-[#F7F7F7] border border-[#E5E5E5] rounded p-5">
      <div className="flex items-center gap-2 mb-2">
        <Shield size={13} className="text-[#D71920]" />
        <span className="text-xs font-bold text-[#0B0B0B] uppercase tracking-wide">Privasi Data</span>
      </div>
      <p className="text-xs text-[#777] leading-relaxed">
        Maklumat yang dihantar hanya digunakan untuk tujuan pendaftaran dan pengesahan oleh Sekretariat RSS Sarawak. Data tidak dikongsi dengan pihak ketiga.
      </p>
    </div>

    {/* Contact */}
    <div className="bg-white border border-[#E5E5E5] rounded p-5">
      <div className="flex items-center gap-2 mb-2">
        <Phone size={13} className="text-[#D71920]" />
        <span className="text-xs font-bold text-[#0B0B0B] uppercase tracking-wide">Bantuan</span>
      </div>
      <p className="text-xs text-[#777]">Untuk pertanyaan, sila hubungi Sekretariat RSS Sarawak.</p>
      <a href="/hubungi" className="text-xs font-semibold text-[#D71920] mt-1 inline-block hover:underline">
        Hubungi Kami →
      </a>
    </div>
  </div>
);

const RegisterSchool = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    head_coach_name: '',
    phone: '',
    email: '',
    division: '',
    district: '',
    training_address: '',
    gelanggang: '',
  });

  const update = (field, val) => {
    setFormData(p => ({ ...p, [field]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error('Sila tandakan kotak pengesahan sebelum menghantar.');
      return;
    }
    setLoading(true);
    setActiveStep(4);
    try {
      let logo_url = null;
      if (logoFile instanceof File) {
        const fd = new FormData();
        fd.append('file', logoFile);
        fd.append('entity_type', 'school');
        const up = await axios.post(`${API_URL}/upload-public?entity_type=school`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        logo_url = up.data.url;
      }
      await axios.post(`${API_URL}/schools`, { ...formData, logo_url });
      setSuccess(true);
      toast.success('Permohonan berjaya dihantar!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menghantar permohonan');
      setActiveStep(0);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSuccess(false);
    setActiveStep(0);
    setConsent(false);
    setLogoFile(null);
    setFormData({ name: '', head_coach_name: '', phone: '', email: '', division: '', district: '', training_address: '', gelanggang: '' });
  };

  if (success) {
    return (
      <PublicFormLayout>
        <SuccessState
          title="Pendaftaran Perguruan Berjaya Dihantar"
          message="Maklumat perguruan anda telah diterima dan akan melalui proses semakan oleh Sekretariat Rumpun Silat Sarawak."
          nextSteps={[
            { label: 'Semakan Sekretariat', desc: 'Pihak RSS akan menyemak semua maklumat yang dihantar.' },
            { label: 'Pengesahan & Kelulusan', desc: 'Perguruan yang lengkap maklumatnya akan diluluskan.' },
            { label: 'Akses Portal Diberikan', desc: 'Emel log masuk dan kata laluan sementara akan dihantar.' },
          ]}
          actions={[
            { label: 'Kembali ke Utama', to: '/', testId: 'success-home-btn' },
            { label: 'Login Portal Ahli', to: '/portal-ahli', testId: 'success-login-btn' },
            { label: 'Daftar Perguruan Lain', onClick: resetForm, testId: 'register-another-button' },
          ]}
        />
      </PublicFormLayout>
    );
  }

  return (
    <PublicFormLayout>
      {/* ── Form Hero ────────────────────────────────────── */}
      <section className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 border border-[#D71920]/30 bg-[#FEF2F2] text-[#D71920] text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-sm mb-5">
              Pendaftaran Rasmi
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-[#0B0B0B] mb-3 leading-tight" data-testid="page-title">
              Daftar Perguruan Silat
            </h1>
            <p className="text-sm sm:text-base text-[#555] mb-6 leading-relaxed">
              Hantar maklumat perguruan anda untuk semakan dan pengesahan oleh Rumpun Silat Sarawak.
            </p>
            {/* Notice */}
            <div className="flex items-start gap-3 p-4 bg-[#FFFBF0] border border-[#D4AF37]/40 rounded">
              <AlertTriangle size={15} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#7a6200] leading-relaxed">
                Semua maklumat yang dihantar akan disemak terlebih dahulu oleh pihak Rumpun Silat Sarawak sebelum disahkan dalam sistem.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Progress Steps ───────────────────────────────── */}
      <div className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <ProgressSteps steps={SCHOOL_STEPS} activeStep={activeStep} />
        </div>
      </div>

      {/* ── Main Form ────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-7">

          {/* Left: Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="school-registration-form">

              {/* Section 01 */}
              <FormSectionCard number="01" title="Maklumat Perguruan">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <FieldWrapper label="Nama Perguruan" required>
                      <StyledInput
                        id="name"
                        value={formData.name}
                        onChange={e => update('name', e.target.value)}
                        required
                        placeholder="Contoh: Perguruan Seni Silat Betawi Kuching"
                        data-testid="input-school-name"
                        onFocus={() => setActiveStep(0)}
                      />
                      <p className="text-xs text-[#777] mt-1.5 leading-relaxed">
                        Pastikan perguruan belum pernah didaftarkan. Jika sudah berdaftar, sila gunakan fungsi kemaskini perguruan.
                      </p>
                    </FieldWrapper>
                  </div>
                  <div>
                    <FieldWrapper label="Bahagian" required>
                      <StyledSelect
                        value={formData.division}
                        onValueChange={val => update('division', val)}
                        placeholder="Pilih Bahagian"
                      >
                        {DIVISIONS.map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </StyledSelect>
                    </FieldWrapper>
                  </div>
                  <div>
                    <FieldWrapper label="Daerah" required>
                      <StyledInput
                        id="district"
                        value={formData.district}
                        onChange={e => update('district', e.target.value)}
                        required
                        placeholder="Contoh: Padawan"
                        data-testid="input-district"
                      />
                    </FieldWrapper>
                  </div>
                </div>
              </FormSectionCard>

              {/* Section 02 */}
              <FormSectionCard number="02" title="Maklumat Guru / Wakil" description="Orang yang bertanggungjawab untuk pendaftaran ini">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <FieldWrapper label="Nama Guru Utama / Ketua Perguruan" required>
                      <StyledInput
                        id="head_coach_name"
                        value={formData.head_coach_name}
                        onChange={e => update('head_coach_name', e.target.value)}
                        required
                        placeholder="Nama penuh"
                        data-testid="input-coach-name"
                        onFocus={() => setActiveStep(1)}
                      />
                    </FieldWrapper>
                  </div>
                  <div>
                    <FieldWrapper label="No Telefon" required>
                      <StyledInput
                        id="phone"
                        value={formData.phone}
                        onChange={e => update('phone', e.target.value)}
                        required
                        placeholder="Contoh: 0123456789"
                        data-testid="input-phone"
                      />
                    </FieldWrapper>
                  </div>
                  <div>
                    <FieldWrapper label="Alamat Emel" required hint="Emel ini digunakan untuk log masuk portal setelah diluluskan.">
                      <StyledInput
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={e => update('email', e.target.value)}
                        required
                        placeholder="emel@perguruan.com"
                        data-testid="input-email"
                      />
                    </FieldWrapper>
                  </div>
                </div>
              </FormSectionCard>

              {/* Section 03 */}
              <FormSectionCard number="03" title="Lokasi & Latihan">
                <div className="space-y-5">
                  <FieldWrapper label="Alamat / Lokasi Gelanggang" required>
                    <StyledInput
                      id="training_address"
                      value={formData.training_address}
                      onChange={e => update('training_address', e.target.value)}
                      required
                      placeholder="Alamat penuh tempat latihan"
                      data-testid="input-address"
                      onFocus={() => setActiveStep(2)}
                    />
                  </FieldWrapper>
                  <FieldWrapper label="Nama Gelanggang" hint="Nama rasmi gelanggang latihan (jika ada)">
                    <StyledInput
                      id="gelanggang"
                      value={formData.gelanggang}
                      onChange={e => update('gelanggang', e.target.value)}
                      placeholder="Contoh: Dewan Serbaguna Kampung Buntal"
                      data-testid="input-gelanggang"
                    />
                  </FieldWrapper>
                  <div className="p-4 bg-[#F7F7F7] border border-[#E5E5E5] rounded text-xs text-[#999]">
                    Maklumat tambahan seperti hari latihan, masa dan bilangan ahli akan dapat dikemaskini kemudian melalui portal perguruan.
                  </div>
                </div>
              </FormSectionCard>

              {/* Section 04 */}
              <FormSectionCard number="04" title="Logo Perguruan" optional>
                <UploadCard
                  value={logoFile}
                  onChange={(f) => { setLogoFile(f); setActiveStep(3); }}
                  entityType="school"
                  hint="Logo digunakan dalam profil rasmi perguruan dalam sistem."
                />
              </FormSectionCard>

              {/* Section 05 — Pengesahan */}
              <FormSectionCard number="05" title="Pengesahan & Penghantaran">
                <div className="space-y-5">
                  <label className="flex items-start gap-3 cursor-pointer group" data-testid="consent-checkbox-label">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={e => { setConsent(e.target.checked); setActiveStep(4); }}
                      className="w-4 h-4 mt-0.5 accent-[#0B0B0B] flex-shrink-0 cursor-pointer"
                      data-testid="consent-checkbox"
                    />
                    <span className="text-sm text-[#555] leading-relaxed group-hover:text-[#0B0B0B] transition-colors">
                      Saya mengesahkan bahawa maklumat yang dihantar adalah benar dan bersetuju untuk disemak oleh pihak <strong>Rumpun Silat Sarawak</strong>.
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={loading || !consent}
                    className="w-full h-12 bg-[#FFD21F] hover:bg-[#f5c800] disabled:bg-[#E5E5E5] disabled:text-[#999] disabled:cursor-not-allowed text-[#0B0B0B] font-extrabold text-sm rounded transition-all flex items-center justify-center gap-2"
                    data-testid="submit-button"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-[#0B0B0B]/20 border-t-[#0B0B0B] rounded-full animate-spin" />
                        Menghantar...
                      </>
                    ) : (
                      <>
                        Hantar Pendaftaran Perguruan
                        <ChevronRight size={16} />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-center text-[#999]">
                    Sudah ada akaun?{' '}
                    <a href="/portal-ahli" className="text-[#D71920] font-semibold hover:underline">
                      Log masuk di sini
                    </a>
                  </p>
                </div>
              </FormSectionCard>

            </form>
          </div>

          {/* Right: Sidebar */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <SchoolSidebar />
            </div>
          </div>
        </div>
      </div>
    </PublicFormLayout>
  );
};

export default RegisterSchool;
